import React from 'react';
import { 
  Heart, 
  Baby, 
  Stethoscope, 
  Scissors, 
  Brain, 
  Bone, 
  Eye,
  Ear,
  TestTube,
  Pill,
  Zap,
  Users
} from 'lucide-react';

const Services: React.FC = () => {
  const services = [
    {
      icon: Zap,
      title: 'Emergency & Casualty',
      description: '24/7 emergency medical services with rapid response team and advanced life support.',
      features: ['Trauma Care', 'Cardiac Emergency', 'Stroke Unit', 'Poison Control']
    },
    {
      icon: Stethoscope,
      title: 'Internal Medicine',
      description: 'Comprehensive care for adult diseases including diabetes, hypertension, and heart conditions.',
      features: ['Diabetes Care', 'Hypertension Management', 'Heart Disease', 'Respiratory Disorders']
    },
    {
      icon: Baby,
      title: 'Pediatrics & Child Health',
      description: 'Specialized medical care for infants, children, and adolescents.',
      features: ['Newborn Care', 'Vaccination', 'Growth Monitoring', 'Pediatric Surgery']
    },
    {
      icon: Scissors,
      title: 'Surgery (General & Specialized)',
      description: 'Advanced surgical procedures performed by experienced surgeons.',
      features: ['General Surgery', 'Orthopedic Surgery', 'Cardiac Surgery', 'Neurosurgery']
    },
    {
      icon: Heart,
      title: 'Obstetrics & Gynecology',
      description: 'Complete women\'s health services including pregnancy care and delivery.',
      features: ['Prenatal Care', 'Delivery Services', 'Family Planning', 'Gynecological Surgery']
    },
    {
      icon: Users,
      title: 'Dental Services',
      description: 'Comprehensive dental care for all ages with modern equipment.',
      features: ['General Dentistry', 'Oral Surgery', 'Orthodontics', 'Cosmetic Dentistry']
    },
    {
      icon: Brain,
      title: 'Psychiatry & Mental Health',
      description: 'Mental health services including counseling and psychiatric treatment.',
      features: ['Depression Treatment', 'Anxiety Disorders', 'Counseling Services', 'Addiction Treatment']
    },
    {
      icon: Bone,
      title: 'Physiotherapy & Rehabilitation',
      description: 'Physical therapy and rehabilitation services for injury recovery.',
      features: ['Sports Injury', 'Stroke Rehabilitation', 'Pain Management', 'Mobility Training']
    },
    {
  icon: Bone, 
  title: 'Occupational Therapy',
  description: 'Helping patients regain independence in daily activities through personalized therapeutic interventions.',
  features: [
    'Daily Living Skills Training',
    'Hand Therapy',
    'Cognitive Rehabilitation',
    'Pediatric Occupational Therapy'
  ]
},
    {
      icon: Eye,
      title: 'Radiology & Diagnostic Imaging',
      description: 'Advanced imaging services for accurate diagnosis.',
      features: ['X-Ray', 'CT Scan', 'MRI', 'Ultrasound']
    },
    {
      icon: Eye,
      title: 'Ophthalmology (Eye Care)',
      description: 'Comprehensive eye care services including vision testing and eye surgery.',
      features: ['Vision Testing', 'Cataract Surgery', 'Glaucoma Treatment', 'Retinal Care']
    },
    {
      icon: Ear,
      title: 'ENT (Ear, Nose & Throat)',
      description: 'Specialized care for ear, nose, and throat conditions.',
      features: ['Hearing Tests', 'Sinus Treatment', 'Throat Surgery', 'Voice Disorders']
    },
    {
      icon: Heart,
      title: 'CTC (Care and Treatment Center)',
      description: 'Comprehensive HIV/AIDS care and treatment services.',
      features: ['HIV Testing', 'ARV Treatment', 'Counseling Services', 'Prevention Programs']
    },
    {
      icon: Users,
      title: 'Mortuary Services',
      description: 'Professional mortuary and funeral services with dignity and respect.',
      features: ['Body Preservation', 'Funeral Arrangements', 'Family Support', 'Documentation']
    },
    {
      icon: TestTube,
      title: 'Laboratory Investigations',
      description: 'Comprehensive laboratory testing with quick and accurate results.',
      features: ['Blood Tests', 'Urine Analysis', 'Microbiology', 'Pathology']
    },
    {
      icon: Pill,
      title: 'Pharmacy Services',
      description: 'Full-service pharmacy with prescription and over-the-counter medications.',
      features: ['Prescription Drugs', 'OTC Medications', 'Drug Counseling', 'Home Delivery']
    }
  ];

  const specialtyServices = [
    {
      title: 'Cardiac Care Center',
      description: 'Specialized heart care with advanced cardiac procedures and monitoring.',
      image: '❤️'
    },
    {
      title: 'Cancer Treatment Center',
      description: 'Comprehensive cancer care including chemotherapy and radiation therapy.',
      image: '🎗️'
    },
    {
      title: 'Kidney Dialysis Unit',
      description: 'Modern dialysis facility for patients with kidney disease.',
      image: '🫘'
    },
    {
      title: 'ICU & Critical Care',
      description: 'Intensive care unit with 24/7 monitoring and life support systems.',
      image: '🏥'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white section-padding">
        <div className="container-max">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Our Medical Services</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Comprehensive healthcare services delivered by experienced medical professionals 
              using state-of-the-art equipment and technology.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Departments & Services</h2>
            <p className="text-xl text-gray-600">
              Our hospital offers a wide range of medical services across multiple specialties
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-1">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-sm text-gray-500 flex items-center">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialty Services */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Specialty Centers</h2>
            <p className="text-xl text-gray-600">
              Advanced medical centers for specialized treatment and care
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {specialtyServices.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">{service.image}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Hours */}
      <section className="section-padding">
        <div className="container-max">
          <div className="bg-blue-600 rounded-2xl text-white p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Service Hours</h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-blue-400 pb-2">
                    <span className="font-medium">Emergency Services</span>
                    <span className="text-blue-100">24/7</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-blue-400 pb-2">
                    <span className="font-medium">Outpatient Services</span>
                    <span className="text-blue-100">24/7</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-blue-400 pb-2">
                    <span className="font-medium">Laboratory</span>
                    <span className="text-blue-100">24/7</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-blue-400 pb-2">
                    <span className="font-medium">Pharmacy</span>
                    <span className="text-blue-100">24/7</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Radiology</span>
                    <span className="text-blue-100">24/7</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-4">Need Medical Attention?</h3>
                  <p className="text-blue-100 mb-6">
                    Our medical team is ready to provide you with the best care possible.
                  </p>
                  <div className="space-y-3">
                    <div className="text-2xl font-bold">+255 658 849 670 </div>
                    <div className="text-blue-100">24/7 Emergency Hotline</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance & Payment */}
      <section className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Insurance & Payment Options</h2>
            <p className="text-xl text-gray-600">
              We accept various insurance plans and payment methods for your convenience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl mb-4">🏛️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Insurance Plans</h3>
              <ul className="text-gray-600 space-y-2">
                <li>NHIF</li>
                <li>Strategis Insurance</li>
                <li>Jubilee Insurance</li>
                <li>AAR Insurance</li>
                <li>Resolution Insurance</li>
                <li>Heritage Insurance</li>
                <li>Britam Insurance</li>
                <li>Madison Insurance</li>
                <li>Phoenix of East Africa</li>
                <li>Corporate Plans</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl mb-4">💳</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Payment Methods</h3>
              <ul className="text-gray-600 space-y-2">
                <li>Cash</li>
                <li>Credit/Debit Cards</li>
                <li>Mobile Money</li>
                <li>Bank Transfer</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl mb-4">❤️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Assistance</h3>
              <ul className="text-gray-600 space-y-2">
                <li>Payment Plans</li>
                <li>Charity Care</li>
                <li>Community Support</li>
                <li>Emergency Fund</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
